import { useEffect, useLayoutEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { DEV_NIGHT_MODE_STORAGE_KEY } from './useDevNightMode';
import {
  DEV_NIGHT_PALETTE_STORAGE_KEY,
  parseDevNightPaletteId,
  type DevNightPaletteId,
} from '../theme/devNightPalettes';

function readSnapshot(): { isDark: boolean; paletteId: DevNightPaletteId } {
  if (typeof document === 'undefined') return { isDark: false, paletteId: 'default' };

  let isDark = document.documentElement.getAttribute('data-dev-night-mode') === 'on';
  try {
    const st = localStorage.getItem(DEV_NIGHT_MODE_STORAGE_KEY);
    if (st === '1') isDark = true;
    if (st === '0') isDark = false;
  } catch {
    /* garder la valeur issue du DOM */
  }

  let paletteId: DevNightPaletteId = 'default';
  const rawAttr = document.documentElement.getAttribute('data-night-palette');
  if (rawAttr != null && rawAttr !== '') {
    paletteId = parseDevNightPaletteId(rawAttr);
  } else {
    try {
      paletteId = parseDevNightPaletteId(localStorage.getItem(DEV_NIGHT_PALETTE_STORAGE_KEY));
    } catch {
      paletteId = 'default';
    }
  }

  return { isDark, paletteId };
}

/**
 * État thème nuit + palette (DOM + localStorage pour les autres onglets).
 * MutationObserver sur `data-dev-night-mode` / `data-night-palette` + événement `storage`.
 */
export function useDevNightThemeSnapshot(): { isDark: boolean; paletteId: DevNightPaletteId } {
  const { user } = useAuth();
  const [{ isDark: storedOrDomDark, paletteId }, setBoth] = useState(readSnapshot);

  const sync = () => setBoth(readSnapshot());

  useLayoutEffect(() => {
    sync();
  }, []);

  useLayoutEffect(() => {
    sync();
  }, [user]);

  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(sync);
    obs.observe(el, {
      attributes: true,
      attributeFilter: ['data-dev-night-mode', 'data-night-palette'],
    });
    const onStorage = (e: StorageEvent) => {
      if (e.key === DEV_NIGHT_MODE_STORAGE_KEY || e.key === DEV_NIGHT_PALETTE_STORAGE_KEY) {
        sync();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      obs.disconnect();
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const isDark = Boolean(user) && storedOrDomDark;

  return { isDark, paletteId };
}
